// Client view of delivery zones and account addresses (Thread C1). The server owns the
// rules (server/services/deliveryZones.ts, server/deliveryZoneRoutes.ts); this is types,
// the query key, and display helpers.
import type { Account, DeliveryZone } from "@shared/schema";
import { deliveryDaysMask } from "@shared/periods";

export const DELIVERY_ZONES_PATH = "/api/delivery-zones";
/** The same key Settings and the account page read the org config by. */
export const ORDER_WINDOW_CONFIG_KEY = ["/api/order-window-config"] as const;

export type DeliveryZoneRow = Omit<DeliveryZone, "createdAt"> & { createdAt: string; accountCount: number };

export interface ZoneListAccount {
  id: number;
  name: string;
  city: string | null;
  /** The town the zone match used: the city, or a free-form address's last line. */
  town: string | null;
  postalCode: string | null;
  addressSource: string | null;
  /** An admin picked this customer's zone (or none) by hand, so their address isn't matched. */
  pinned: boolean;
  /** Every zone the customer is in. They are listed under each, and get all of their days. */
  zoneIds: number[];
}

export interface DeliveryZonesResponse {
  zones: DeliveryZoneRow[];
  /** One list of customers: Unzoned (zoneId null), or one zone's (GET ?zone=<id>). */
  list: { accounts: ZoneListAccount[]; total: number; limit: number; zoneId: number | null } | null;
  /** The counts and lists stopped at the server's cap on customers looked at. */
  capped?: boolean;
}

/** GET /accounts/:id/delivery-days — one customer's delivery days and where they come from. */
export interface AccountDeliveryDays {
  accountId: number;
  /** What the customer is offered right now. */
  deliveryDays: number;
  orgDays: number;
  /** False while "Delivery days by customer" says "Same for everyone". */
  zonesApplied: boolean;
  /** What the zones give, applied or not. */
  zoneDays: number;
  /** 'manual': an admin picked one zone, or none. 'address': every zone listing the address. */
  source: "manual" | "address";
  zones: Array<{ id: number; name: string; deliveryDays: number }>;
  town: string | null;
  postalArea: string | null;
}

/** The select value for "the address decides" and for "no zone, by hand". */
export const ZONE_PICK_MATCH = "match";
export const ZONE_PICK_NONE = "none";

/** The body PUT /accounts/:id/delivery-zone takes for a zone-pick select value. */
export function zonePickBody(value: string): { match: "address" } | { deliveryZoneId: number | null } {
  if (value === ZONE_PICK_MATCH) return { match: "address" };
  return { deliveryZoneId: value === ZONE_PICK_NONE ? null : Number(value) };
}

/** The fields of the org's order-window config the zone UI reads. */
export interface ZoneScheduleConfig {
  id: number;
  frequency: string;
  customDays: number;
  deliveryEnabled?: boolean;
  deliveryDaysByZone?: boolean;
}

export const WEEKDAYS = [
  { bit: 0, short: "Sun", long: "Sunday" },
  { bit: 1, short: "Mon", long: "Monday" },
  { bit: 2, short: "Tue", long: "Tuesday" },
  { bit: 3, short: "Wed", long: "Wednesday" },
  { bit: 4, short: "Thu", long: "Thursday" },
  { bit: 5, short: "Fri", long: "Friday" },
  { bit: 6, short: "Sat", long: "Saturday" },
] as const;

/** Monday first, as the week reads here. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** The org's delivery days (bitmask) — what a zone can pick from. */
export function orgDeliveryDays(config: Pick<ZoneScheduleConfig, "frequency" | "customDays"> | undefined): number {
  return config ? deliveryDaysMask(config) : 0;
}

/** "Mon, Wed, Fri"; "No days" for 0. Monday first, as the week reads here. */
export function daysLabel(mask: number): string {
  const days = WEEK_ORDER.filter((bit) => mask & (1 << bit)).map((bit) => WEEKDAYS[bit].short);
  return days.length > 0 ? days.join(", ") : "No days";
}

/**
 * The days a customer in these zones can choose: every day any of them delivers, out of
 * the org's. As the server works it out (zonedDeliveryDays) for a customer in a zone.
 */
export function stackedDays(zones: readonly { deliveryDays: number }[], orgDays: number): number {
  return zones.reduce((mask, zone) => mask | zone.deliveryDays, 0) & orgDays;
}

/**
 * Zones in the order a printed schedule reads: by first delivery day (Monday first),
 * a zone with fewer days before one with more that starts the same day, then as they
 * were added. A zone's days are the ones it still shares with the org's, when it
 * shares any; a zone with no days at all goes last.
 */
export function zonesByFirstDay<Z extends { id: number; deliveryDays: number; sortOrder: number }>(zones: readonly Z[], orgDays: number): Z[] {
  const key = (zone: Z) => {
    const days = zone.deliveryDays & orgDays || zone.deliveryDays;
    const on = WEEK_ORDER.filter((bit) => days & (1 << bit));
    return { first: on.length > 0 ? WEEK_ORDER.indexOf(on[0]) : WEEK_ORDER.length, count: on.length };
  };
  const keyed = zones.map((zone) => ({ zone, ...key(zone) }));
  keyed.sort((a, b) => a.first - b.first || a.count - b.count || a.zone.sortOrder - b.zone.sortOrder || a.zone.id - b.zone.id);
  return keyed.map((k) => k.zone);
}

/** Zones can vary days only on a schedule with more than one delivery day (brief §5 row 10). */
export function scheduleAllowsZones(config: Pick<ZoneScheduleConfig, "frequency"> | undefined): boolean {
  return config?.frequency === "custom" || config?.frequency === "daily";
}

export type AccountAddress = Pick<
  Account,
  "addressLine1" | "addressLine2" | "city" | "province" | "postalCode" | "addressSource" | "quickbooksCustomerId"
> & { addressRefreshedAt: string | Date | null };

/** "412 10th St, Hanover ON N0G 1A0", or null when there is no address. */
export function formatAddress(a: Pick<AccountAddress, "addressLine1" | "addressLine2" | "city" | "province" | "postalCode">): string | null {
  const place = [a.city, [a.province, a.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(" ");
  const parts = [a.addressLine1, a.addressLine2, place].filter((p): p is string => !!p && p.trim() !== "");
  return parts.length > 0 ? parts.join(", ") : null;
}

/** The grey line under the address: where it came from. */
export function addressSourceLabel(a: Pick<AccountAddress, "addressSource" | "addressRefreshedAt">): string | null {
  if (a.addressSource === "manual") return "Entered by hand";
  if (a.addressSource === "quickbooks") {
    const when = a.addressRefreshedAt ? new Date(a.addressRefreshedAt) : null;
    return when && !Number.isNaN(when.getTime())
      ? `From QuickBooks · refreshed ${when.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
      : "From QuickBooks";
  }
  return null;
}

/** "N0G, N0H" → ["N0G", "N0H"]; the server normalises and checks each. */
export function splitList(text: string): string[] {
  return text
    .split(/[,\n;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface AddressRefreshResult {
  linked: number;
  refreshed: number;
  changed: number;
  skippedManual: number;
  notInQuickBooks: number;
  noAddress: number;
}

/** One sentence for the toast after a refresh. */
export function refreshSummary(r: AddressRefreshResult): string {
  if (r.linked === 0) return "No accounts are linked to QuickBooks customers yet, so there was nothing to refresh.";
  const parts = [`${r.refreshed} of ${r.linked} linked account${r.linked === 1 ? "" : "s"} refreshed, ${r.changed} with a new address.`];
  if (r.skippedManual > 0) parts.push(`${r.skippedManual} entered by hand were left alone.`);
  if (r.notInQuickBooks > 0) parts.push(`${r.notInQuickBooks} ${r.notInQuickBooks === 1 ? "isn't" : "aren't"} in QuickBooks any more.`);
  if (r.noAddress > 0) parts.push(`${r.noAddress} ${r.noAddress === 1 ? "has" : "have"} no address in QuickBooks.`);
  return parts.join(" ");
}
